<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { ColumnReadModel, ValueSerieReadModel } from "@Quest/_Common/Domain/readmodels";
    import { MonthYear, MonthYearHelpers } from "@common/types";

    @Component({
        props: {
            propTitle: { type: String, required: false },
            propSeries: { type: Array, required: true },
            propColumns: { type: Array, required: true },
            propStartDate: { type: Object, required: false },
        }
    })
    export default class ValueSeriesTable extends Vue
    {
        get title(): string { return this.$props.propTitle; }
        get series(): ValueSerieReadModel[] { return this.$props.propSeries as ValueSerieReadModel[]; }
        get columns(): ColumnReadModel[] { return this.$props.propColumns as ColumnReadModel[]; }
        get startDate(): MonthYear | null { return this.$props.propStartDate as MonthYear ?? null; }
        
        get totalSerie() { return this.series.find(s => s.IsTotal); }
        get regularSeries() { return this.series.filter(s => !s.IsTotal); }
        get startDateLabel(): string | null { return MonthYearHelpers.toMonthYearLongString(this.startDate); }
    }
</script>

<template>
    <div>
        <div class="table-wrapper">
            <table class="table table-bordered">
                <thead>
                    <tr v-if="startDate">
                        <td class="sticky-col">
                            Start date: <span class="font-italic pointer" title="Dated labels align project periods (e.g., months, quarters, or years) with calendar dates based on the project start date.">
                                {{startDateLabel}}
                            </span>
                        </td>
                        <td class="period-col" v-for="col in columns" :key="col.Index" :class="col.CssClass">
                            {{col.DatedLabel}}
                        </td>
                    </tr>
                    <tr>
                        <th class="sticky-col">
                            {{ title }}
                        </th>
                        <th class="period-col" v-for="col in columns" :key="col.Index">
                            {{col.Label}}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="s in regularSeries" :key="s.Id">
                        <td class="sticky-col">
                            <span>{{s.Name}}</span>
                            <span v-if="s.Unit">[{{s.Unit}}]</span>
                        </td>
                        <td class="period-col" v-for="col in columns" :key="col.Index">
                            <span>{{ s.FormattedValues[col.Index] }}</span>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="totalSerie">
                    <tr>
                        <th class="sticky-col">
                            <span>{{totalSerie.Name}}</span>
                            <span v-if="totalSerie.Unit">[{{totalSerie.Unit}}]</span>
                        </th>
                        <td class="period-col" v-for="col in columns" :key="col.Index">
                            <span>{{ totalSerie.FormattedValues[col.Index]}}</span>
                        </td>
                    </tr>
                    <tr v-if="totalSerie.ShowGrowthValues">
                        <th class="sticky-col">
                            % Growth
                        </th>
                        <td class="period-col" v-for="col in columns" :key="col.Index">
                            <span>{{ totalSerie.FormattedGrowthValues[col.Index] }}</span>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>      


<style scoped>
    .table-wrapper {
        overflow-x: auto;
        max-width: 100%;
        margin-bottom: 100px;
    }

    .table {
        margin-bottom: 0;
        position: relative;
        z-index: 1;
    }

    .sticky-col {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: white;
        width: 350px;
        min-width: 350px;
    }


    /* Styling for month headers */
    .period-col {
        text-align: center;
        font-weight: 500;
        border-right: 1px solid #f5f5f5;
    }

    td.period-col {
        padding: 2px;
        vertical-align:middle;
    }

    /* Past Period: Light Gray */
    .past {
        background-color: #e0e0e0; /* Light gray */
        color: #555555; /* Optional: Darker text for contrast */
    }

    /* Current Period (Now): Soft Blue */
    .now {
        background-color: #a2c4f2; /* Soft blue */
        border: 1px solid #5a9bd5; /* Optional: Slightly darker blue border */
    }

    /* Commitment Period: Light Blue */
    .commitment {
        background-color: #d3e5fa; /* Light blue */
        border: 1px solid #a2c4f2; /* Optional: Slightly darker border */
    }

    /* Future Period: Pale Blue */
    .future {
        background-color: #ebf4fc; /* Pale blue */
        border: 1px dashed #d3e5fa; /* Optional: Dashed border for distinction */
    }


</style>